import { useEffect, useState } from 'react';
import { postAction, isConnectivityError } from '../api/client';
import type { TagResolveOk, TagResolveResponse } from '../api/types';
import { isErr } from '../api/types';
import { resolveTagOffline, resolveTagLocal, primeSnapshot } from '../db/snapshot';

const FRIENDLY: Record<string, string> = {
  TAG_NOT_FOUND: 'This tag is not registered. Show the code below to your supervisor.',
  TAG_INACTIVE: 'This tag has been deactivated. Use the current tag fixed to this equipment.',
  TAG_BAD_SIGNATURE: 'This tag could not be verified. It may be a copy. Contact your supervisor.',
  EQUIPMENT_INACTIVE: 'This equipment is marked inactive in the system.',
  OFFLINE_UNKNOWN: 'Cannot reach the server, and this tag has not been used on this phone before. Move to where there is signal and try again.',
};

export interface TagState {
  loading: boolean;
  data?: TagResolveOk;
  offline?: boolean;
  /** data came from the local snapshot and the server check is still in flight */
  provisional?: boolean;
  error?: string;
  rawError?: string;
}

// in-memory cache with a short TTL so scan -> sign-in -> form does not re-hit the network
const TTL_MS = 90_000;
const cache = new Map<string, { at: number; data: TagResolveOk }>();
export function cachedTag(code: string): TagResolveOk | undefined {
  const hit = cache.get(code.toUpperCase());
  return hit && Date.now() - hit.at < TTL_MS ? hit.data : undefined;
}
function putCache(code: string, data: TagResolveOk) {
  cache.set(code.toUpperCase(), { at: Date.now(), data });
}
/** Drop the cached resolve for a tag (e.g. right after a reading is submitted for it). */
export function invalidateTag(code: string) {
  cache.delete(code.toUpperCase());
}

export function useTagResolve(code: string, signature: string | null): TagState {
  const [state, setState] = useState<TagState>(() => {
    const c = cachedTag(code);
    if (c) return { loading: false, data: c };
    const local = resolveTagLocal(code);
    return local ? { loading: false, data: local, provisional: true } : { loading: true };
  });

  useEffect(() => {
    let alive = true;
    let settled = false; // the authoritative server response (or error) has landed
    const fresh = cachedTag(code);
    if (fresh) { setState({ loading: false, data: fresh }); return; }
    // paint the local snapshot immediately; the server call below reconciles it
    let seed = resolveTagLocal(code);
    setState(seed ? { loading: false, data: seed, provisional: true } : { loading: true });

    // first scan right after a cold start: the in-memory snapshot may not be
    // loaded yet — pull it from IndexedDB and show it if the server hasn't answered
    if (!seed) {
      void primeSnapshot().then(() => {
        if (!alive || settled) return;
        const s = resolveTagLocal(code);
        if (s) { seed = s; setState({ loading: false, data: s, provisional: true }); }
      });
    }

    (async () => {
      try {
        const res = await postAction<TagResolveResponse>('tag', { code, k: signature ?? undefined });
        if (!alive) return;
        settled = true;
        if (isErr(res)) {
          // a stale local seed shouldn't turn a working screen into an error page
          if (seed && (res.error === 'OFFLINE_UNKNOWN')) return;
          setState({ loading: false, error: FRIENDLY[res.error] ?? res.message, rawError: res.error });
        } else {
          putCache(code, res);
          setState({ loading: false, data: res });
        }
      } catch (e) {
        if (!alive) return;
        settled = true;
        if (isConnectivityError(e)) {
          const off = await resolveTagOffline(code);
          if (!alive) return;
          if (off) {
            putCache(code, off);
            setState({ loading: false, data: off, offline: true });
          } else if (seed) {
            setState({ loading: false, data: seed, offline: true });
          } else {
            setState({ loading: false, error: FRIENDLY.OFFLINE_UNKNOWN, rawError: 'OFFLINE_UNKNOWN' });
          }
        } else if (seed) {
          setState({ loading: false, data: seed, offline: true });
        } else {
          setState({ loading: false, error: e instanceof Error ? e.message : String(e), rawError: 'UNKNOWN' });
        }
      }
    })();

    return () => { alive = false; };
  }, [code, signature]);

  return state;
}
